<template>
  <div>
    <SnackbarComponent v-model="snackbar.show" :message="snackbar.message" :color="snackbar.color" />
    <ImageCropperDialog v-model="cropDialog" :image-file="cropFile" @cropped="handleCropped"
      @cancel="handleCropCancel" />
    <v-dialog v-model="dialog" max-width="1300" persistent>
      <v-card class="blog-dialog">
        <v-card-title class="text-h5 pa-5">
          Add Blog Post
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0 d-flex flex-column">
          <v-form ref="form" class="h-100 d-flex flex-column">
            <v-row no-gutters class="dialog-content">
              <!-- LEFT -->
              <v-col cols="12" md="4" class="left-panel pa-5" style="border-right:1px solid #e0e0e0;">

                <v-text-field v-model="formData.blog_author_name" label="Author Name *" variant="outlined"
                  density="compact" :rules="[(v) => !!v || 'Author Name is required']" class="mb-2" />

                <v-text-field v-model="formData.blog_main_title" label="Blog Title *" variant="outlined"
                  density="compact" :rules="[(v) => !!v || 'Blog Title is required']" class="mb-2" />

                <!-- Publish Date -->
                <v-text-field v-model="publishDateDisplay" label="Publish Date *" placeholder="MM/DD/YYYY"
                  variant="outlined" density="compact" maxlength="10" class="mb-2" @input="(e) => formatDateInput(e)"
                  @blur="validateAndFormatDate">
                  <template #append-inner>
                    <v-menu v-model="showPublishPicker" :close-on-content-click="false">
                      <template #activator="{ props }">
                        <v-icon v-bind="props" @mousedown.prevent>
                          mdi-calendar
                        </v-icon>
                      </template>

                      <v-date-picker v-model="tempPublishDate" hide-header @update:model-value="setPublishDate" />
                    </v-menu>
                  </template>
                </v-text-field>

                <!-- <v-text-field v-model="formData.blog_category" label="Category " variant="outlined" density="compact"
                  class="mb-2" /> -->

                <div class="text-subtitle-1 font-weight-medium mb-3">
                  Cover Image
                </div>

                <v-img v-if="coverImageUrl" :src="coverImageUrl" height="230" cover class="rounded-lg mb-4" />

                <v-sheet v-else height="230" border rounded class="d-flex align-center justify-center mb-4">
                  <div class="text-medium-emphasis">
                    No Cover Image Selected
                  </div>
                </v-sheet>

                <v-btn block color="primary" prepend-icon="mdi-image-plus" :loading="uploading"
                  @click="$refs.fileInput.click()">
                  Select Cover Image
                </v-btn>

                <input ref="fileInput" type="file" class="d-none" accept="image/*" @change="onFileSelected" />

              </v-col>

              <!-- RIGHT -->
              <v-col cols="12" md="8" class="pa-6 right-panel">
                <div class="right-scroll">
                  <div class="rich-text-editor">
                    <QuillEditor v-model:content="formData.blog_content" contentType="html" theme="snow"
                      class="blog-editor" />
                  </div>
                </div>
              </v-col>

            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />

          <v-btn variant="text" @click="handleClose" color="grey">
            Cancel
          </v-btn>

          <v-btn color="primary" variant="flat" :loading="loading" @click="handleSubmit">
            Create Blog Post
          </v-btn>

        </v-card-actions>

      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import { generateClient } from "aws-amplify/api";
import { create_blog } from "@/graphql/mutations";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";
import S3AmplifyMixin from "@/mixins/S3Amplify";
import ImageCropperDialog from "@/components/Extras/ImageCropperDialog.vue";
import getBaseFolder from "@/Configurations/s3BaseFolder";
const client = generateClient();

export default {
  name: "CreateBlogDialog",
  components: {
    SnackbarComponent,
    ImageCropperDialog,
    QuillEditor,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [S3AmplifyMixin],
  emits: ["update:modelValue", "created"],
  data() {
    return {
      cropDialog: false,
      cropFile: null,

      pendingCroppedFile: null,
      pendingPreviewUrl: null,

      coverImageUrl: "",
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
      publishDate: "",
      publishDateDisplay: "",
      tempPublishDate: null,
      showPublishPicker: false,
      loading: false,
      uploading: false,
      formData: {
        blog_main_title: "",
        blog_category: "",
        blog_author_name: "",
        blog_content: "",
        blog_image_s3_path: "",
        published_on: "",
      },
    };
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.resetForm();

        const today = new Date();

        this.tempPublishDate = today;

        const y = today.getFullYear();
        const m = String(today.getMonth() + 1).padStart(2, "0");
        const d = String(today.getDate()).padStart(2, "0");

        this.publishDateDisplay = `${m}/${d}/${y}`;
        this.publishDate = Math.floor(
          new Date(`${y}-${m}-${d}T00:00:00`).getTime() / 1000
        );
        this.formData.published_on = this.publishDate;
      }
    },
  },
  methods: {
    generateBlogPageUrl(title) {
      const slug = (title || "")
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
        .replace(/\s+/g, "-")         // Replace spaces with -
        .replace(/-+/g, "-");         // Remove duplicate hyphens

      return `https://rcshealth.io/blog-post/${slug}`;
    },
    formatDateInput(e) {
      let value = e.target.value.replace(/\D/g, "").slice(0, 8);

      if (value.length > 2) {
        value = value.slice(0, 2) + "/" + value.slice(2);
      }

      if (value.length > 5) {
        value = value.slice(0, 5) + "/" + value.slice(5);
      }

      this.publishDateDisplay = value;
    },

    validateAndFormatDate() {
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(this.publishDateDisplay)) return;

      const [m, d, y] = this.publishDateDisplay.split("/");

      this.publishDate = Math.floor(
        new Date(`${y}-${m}-${d}T00:00:00`).getTime() / 1000
      );

      this.formData.published_on = this.publishDate;
    },

    setPublishDate(val) {
      const date = new Date(val);

      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");

      this.publishDateDisplay = `${m}/${d}/${y}`;

      this.publishDate = Math.floor(
        new Date(`${y}-${m}-${d}T00:00:00`).getTime() / 1000
      );

      this.formData.published_on = this.publishDate;

      this.showPublishPicker = false;
    },
    async onFileSelected(event) {
      const file = event.target.files?.[0];

      if (!file) return;

      this.cropFile = file;
      this.cropDialog = true;

      event.target.value = "";
    },
    async uploadBlogCover(file) {
      const key = `${getBaseFolder()}/BlogImages/${Date.now()}-${file.name}`;

      return this.uploadFile(file, key, "landing");
    },
    handleCropCancel() {
      this.cropDialog = false;
      this.cropFile = null;
    },
    handleCropped(file) {
      this.cropDialog = false;

      if (this.pendingPreviewUrl) {
        URL.revokeObjectURL(this.pendingPreviewUrl);
      }

      this.pendingCroppedFile = file;
      this.pendingPreviewUrl = URL.createObjectURL(file);

      // Show preview immediately
      this.coverImageUrl = this.pendingPreviewUrl;
    },
    handleClose() {
      this.dialog = false;
      this.resetForm();
    },
    resetForm() {
      this.formData = {
        blog_main_title: "",
        blog_category: "",
        blog_author_name: "",
        blog_content: "",
        blog_image_s3_path: "",
      };
      this.publishDate = "";
      this.publishDateDisplay = "";
      this.tempPublishDate = null;
      this.showPublishPicker = false;
      this.cropDialog = false;
      this.coverImageUrl = "";
      this.cropFile = null;
      this.pendingCroppedFile = null;

      if (this.pendingPreviewUrl) {
        URL.revokeObjectURL(this.pendingPreviewUrl);
        this.pendingPreviewUrl = null;
      }

      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    async handleSubmit() {
      const plainText = (this.formData.blog_content || "")
        .replace(/<[^>]*>/g, "")
        .trim();

      if (!plainText) {
        this.snackbar = {
          show: true,
          message: "Blog Description is required",
          color: "error",
        };
        return;
      }
      const { valid } = await this.$refs.form.validate();

      if (!valid) return;
      this.loading = true;
      let coverImagePath = this.formData.blog_image_s3_path;

      if (this.pendingCroppedFile) {

        try {
          this.uploading = true;

          if (this.pendingCroppedFile) {
            coverImagePath = await this.uploadBlogCover(this.pendingCroppedFile);
            this.formData.blog_image_s3_path = coverImagePath;
          }
        } finally {
          this.uploading = false;
        }
      }

      try {
        const payload = JSON.stringify(this.formData.blog_content);
        const blogPageUrl = this.generateBlogPageUrl(
          this.formData.blog_main_title
        );
        const input = {
          blog_main_title: this.formData.blog_main_title,
          blog_author_name: this.formData.blog_author_name,
          blog_content: payload,
          blog_image_s3_path: coverImagePath,
          blog_page_url: blogPageUrl,
          published_on: this.publishDate,

        };

        const response = await client.graphql({
          query: create_blog,
          variables: {
            input: input,
          },
        });

        const responseData = JSON.parse(response.data.create_blog);

        if (responseData.status === "SUCCESS") {
          this.snackbar = {
            show: true,
            message: responseData.status_message || "Blog post created successfully!",
            color: "success",
          };
          this.$emit("created");
          this.handleClose();
        } else {
          this.snackbar = {
            show: true,
            message: responseData.status_message || "Failed to create Blog Post",
            color: "error",
          };
        }
      } catch (error) {
        console.error("Error creating Blog Post:", error);
        this.snackbar = {
          show: true,
          message:
            (error.errors && error.errors[0]?.message) ||
            error.message ||
            "An error occurred while creating Blog Post",
          color: "error",
        };
      } finally {
        this.loading = false;
      }
    },
  },
};

</script>

<style>
.blog-dialog {
  height: 85vh;
  display: flex;
  flex-direction: column;
}

.blog-dialog .v-card-text {
  flex: 1;
  overflow: hidden;
  padding: 0;
  min-height: 0;
}

.dialog-content {
  height: 100%;
  margin: 0;
}

/* ---------------- LEFT PANEL ---------------- */

.left-panel {
  height: 100%;
  border-right: 1px solid #e0e0e0;

  overflow-y: auto;
  overflow-x: hidden;

  padding: 20px;
}

/* ---------------- RIGHT PANEL ---------------- */

.right-panel {
  height: 100%;
  display: flex;
  overflow: hidden;
  padding: 20px;
}

.right-scroll {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* ---------------- QUILL ---------------- */

.rich-text-editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.blog-editor {
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
}

/* Toolbar */

:deep(.ql-toolbar.ql-snow) {
  position: sticky;
  top: 0;
  z-index: 10;

  background: #fff;

  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  border-bottom: 1px solid #ddd;
}

/* Container */

:deep(.ql-container.ql-snow) {
  flex: 1;
  overflow-y: auto;

  border: none;

  font-size: 15px;
}

/* Editor */

:deep(.ql-editor) {
  min-height: 100%;
  padding: 18px;

  font-size: 15px;
  line-height: 1.8;
}

/* Footer */

.blog-dialog .v-card-actions {
  flex-shrink: 0;
  border-top: 1px solid #eee;
  background: white;
}

/* Scrollbars */

.left-panel::-webkit-scrollbar,
:deep(.ql-container)::-webkit-scrollbar {
  width: 8px;
}

.left-panel::-webkit-scrollbar-thumb,
:deep(.ql-container)::-webkit-scrollbar-thumb {
  background: #c7c7c7;
  border-radius: 8px;
}

.left-panel::-webkit-scrollbar-thumb:hover,
:deep(.ql-container)::-webkit-scrollbar-thumb:hover {
  background: #999;
}
</style>